// @givmo/board — "Record a term" and "Correct" on a member's page (CBG-YR3-TERMS; the backend's
// contract `board-terms.md` §6.2–§6.4). No frame drew these dialogs: they take the result dialog's
// fields (t5) for one person.
//
//   * The organization's own dates, at least one; an end never before the start — said here as the
//     backend says it, and refused there too (its typed 422s, shown as served).
//   * A class from the board's own list, when it has any. Correcting a term keeps a retired class it
//     names in the list, so a save never drops it unseen.
//   * "Counts toward the limit", only when the board recorded a limit (or the term is already marked
//     not counted, so the mark stays visible and can be undone).
//   * Recording, and only recording, may link the minute-book record the term came from: an approved
//     record from the book. The source is never corrected — a term from the wrong record is removed
//     and recorded again (§6.3).
//   * Recording a seat's FIRST term (the seat has none yet) says, under the dates, where a founding
//     director's date usually comes from, its detail behind "Where to find it" (CBG-UX5-HINT,
//     `term-hint.tsx`), without "Add a member"'s "Not at hand? …" line: this is their page, and
//     a term is recorded with at least one date.
//   * Correcting sends only what changed (`null` clears a date, the class or the note), so a save that
//     changes nothing records nothing.
//   * "Remove this term" asks first and is for a term recorded by mistake (§6.4, C2): nothing it came
//     from changes.
//
// Saved as entered: Board never refuses a term for overlapping another, running past the board's
// recorded length or limit (§6.2). Writing a term is the Owner's act (`board_member:manage`).

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardDirectorClassOut,
  type BoardMemberTermOut,
  type OrgBoardMemberOut,
} from '@givmo/api-client';
import {
  Button,
  Checkbox,
  Dialog,
  ErrorMessage,
  Select,
  TextField,
  Textarea,
  toast,
} from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { displayDate } from '../../lib/display';
import { extractRecords, isTheRecord, recordKindLabel } from '../records/records-decode';
import { TermHint } from './term-hint';
import { currentTerm, seatTerms } from './terms-decode';
import { termsWords as tw } from './terms-words';

const NO_CHOICE = 'none';
const BOOK_KINDS = new Set(['resolution', 'minutes', 'written_consent']);

/** What the dialog checks before it sends: the backend's own two rules, in its words. */
export function termDatesProblem(start: string, end: string): string | null {
  if (start.length === 0 && end.length === 0) return tw.needADate;
  if (start.length > 0 && end.length > 0 && end < start) return tw.endBeforeStart;
  return null;
}

/** A correction's body: only the fields that differ from the stored term. */
export function termChanges(
  term: BoardMemberTermOut,
  next: { start: string; end: string; classId: string | null; counts: boolean; note: string },
): Record<string, string | boolean | null> {
  const changes: Record<string, string | boolean | null> = {};
  const startOrNull = next.start.length > 0 ? next.start : null;
  const endOrNull = next.end.length > 0 ? next.end : null;
  const noteOrNull = next.note.trim().length > 0 ? next.note.trim() : null;
  if (startOrNull !== (term.starts_on ?? null)) changes.starts_on = startOrNull;
  if (endOrNull !== (term.ends_on ?? null)) changes.ends_on = endOrNull;
  if (next.classId !== (term.class_id ?? null)) changes.class_id = next.classId;
  if (next.counts !== (term.counts_toward_limit !== false))
    changes.counts_toward_limit = next.counts;
  if (noteOrNull !== (term.note ?? null)) changes.note = noteOrNull;
  return changes;
}

export function TermDialog({
  orgRef,
  seat,
  term,
  classes,
  limitRecorded,
  onClose,
  onDone,
}: {
  orgRef: string;
  seat: OrgBoardMemberOut;
  /** `null` to record a new term; a term to correct it. */
  term: BoardMemberTermOut | null;
  classes: readonly BoardDirectorClassOut[];
  limitRecorded: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const operations = useBoardOperations();
  const correcting = term !== null;
  // CBG-UX5-HINT: a seat with no term yet is often a founding director's.
  const firstTerm = !correcting && seatTerms(seat).length === 0;
  const [start, setStart] = useState(term?.starts_on ?? '');
  const [end, setEnd] = useState(term?.ends_on ?? '');
  // A correction starts from the term's own class; a new term from the seat's current one, as the
  // result on the roster does, unless the board has retired it.
  const [classId, setClassId] = useState<string | null>(() => {
    if (term !== null) return term.class_id ?? null;
    const current = currentTerm(seat);
    return current?.class_status === 'retired' ? null : (current?.class_id ?? null);
  });
  const [counts, setCounts] = useState(term?.counts_toward_limit !== false);
  const [note, setNote] = useState(term?.note ?? '');
  const [recordId, setRecordId] = useState<string | null>(null);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The classes offered: the active ones, plus a retired one this term already names.
  const classOptions = useMemo(
    () =>
      classes
        .filter((row) => row.status !== 'retired' || row.id === term?.class_id)
        .map((row) => ({ value: row.id, label: row.label })),
    [classes, term?.class_id],
  );

  // Recording only: the minute book's records a term can come from (the first page, newest first).
  const recordsQuery = useQuery({
    ...operations.officerListBoardRecordsOptions({
      path: { org_ref: orgRef },
      query: { 'page[limit]': 100 },
    }),
    enabled: !correcting,
    select: (body: unknown) =>
      extractRecords(body).filter(
        (record) => isTheRecord(record) && BOOK_KINDS.has((record.record_kind ?? '').toLowerCase()),
      ),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (term !== null) {
        await operations.officerUpdateBoardMemberTerm({
          path: { org_ref: orgRef, member_id: seat.id, term_id: term.id },
          body: termChanges(term, { start, end, classId, counts, note }),
          throwOnError: true,
        });
        return;
      }
      await operations.officerRecordBoardMemberTerm({
        path: { org_ref: orgRef, member_id: seat.id },
        body: {
          ...(start.length > 0 ? { starts_on: start } : {}),
          ...(end.length > 0 ? { ends_on: end } : {}),
          ...(classId !== null ? { class_id: classId } : {}),
          counts_toward_limit: counts,
          ...(recordId !== null ? { record_id: recordId } : {}),
          ...(note.trim().length > 0 ? { note: note.trim() } : {}),
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(correcting ? tw.termCorrected : tw.termRecorded);
      onDone();
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : tw.termFailed),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (term === null) return;
      await operations.officerRemoveBoardMemberTerm({
        path: { org_ref: orgRef, member_id: seat.id, term_id: term.id },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(tw.termRemoved);
      onDone();
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : tw.termFailed),
  });

  const pending = save.isPending || remove.isPending;
  const problem = termDatesProblem(start, end);
  const unchanged =
    term !== null &&
    Object.keys(termChanges(term, { start, end, classId, counts, note })).length === 0;
  const heading = confirmingRemoval
    ? tw.removeTitle
    : correcting
      ? tw.correctTitle
      : tw.recordTitle;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onClose()}>
      <Dialog.Content
        aria-label={heading}
        className="flex flex-col gap-16"
        testId={correcting ? 'member-term-correct-dialog' : 'member-term-record-dialog'}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {heading}
        </Dialog.Title>
        <span className="-mt-8 font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
          {seat.display_name}
        </span>

        {confirmingRemoval ? (
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {tw.removeBody}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-12">
              <TextField
                label={tw.start}
                type="date"
                value={start}
                disabled={pending}
                onChange={(event) => setStart(event.target.value)}
                testId="member-term-start"
              />
              <TextField
                label={tw.end}
                type="date"
                value={end}
                disabled={pending}
                onChange={(event) => setEnd(event.target.value)}
                testId="member-term-end"
              />
            </div>
            <FieldNote>{problem ?? tw.datesNote}</FieldNote>
            {firstTerm ? <TermHint testId="member-term-hint" later={false} /> : null}

            {classOptions.length > 0 ? (
              <Select
                label={tw.classLabel}
                appearance="saas"
                value={classId ?? NO_CHOICE}
                disabled={pending}
                onValueChange={(next) =>
                  setClassId(next === null || next === NO_CHOICE ? null : next)
                }
                options={[{ value: NO_CHOICE, label: tw.noClass }, ...classOptions]}
                testId="member-term-class"
              />
            ) : null}

            {limitRecorded || !counts ? (
              <label className="inline-flex cursor-pointer items-center gap-10">
                <Checkbox
                  appearance="saas"
                  checked={counts}
                  disabled={pending}
                  onCheckedChange={(next) => setCounts(next === true)}
                  testId="member-term-counts"
                />
                <span className="font-sans text-s14 font-w400 tracking-givmo text-text-strong">
                  {tw.countsTowardLimit}
                </span>
              </label>
            ) : null}

            {!correcting && (recordsQuery.data ?? []).length > 0 ? (
              <Select
                label={tw.bookRecord}
                appearance="saas"
                value={recordId ?? NO_CHOICE}
                disabled={pending}
                onValueChange={(next) =>
                  setRecordId(next === null || next === NO_CHOICE ? null : next)
                }
                options={[
                  { value: NO_CHOICE, label: tw.noBookRecord },
                  ...(recordsQuery.data ?? []).map((record) => ({
                    value: record.id,
                    label: `${record.title} · ${recordKindLabel(record.record_kind)}${
                      record.occurred_on ? ` · ${displayDate(record.occurred_on)}` : ''
                    }`,
                  })),
                ]}
                testId="member-term-record"
              />
            ) : null}

            <Textarea
              label={tw.note}
              value={note}
              maxLength={500}
              rows={2}
              disabled={pending}
              onChange={(event) => setNote(event.target.value)}
              testId="member-term-note"
            />
          </>
        )}

        {error !== null ? <ErrorMessage testId="member-term-error">{error}</ErrorMessage> : null}

        <div className="flex flex-wrap items-center justify-end gap-10">
          {correcting && !confirmingRemoval ? (
            <Button
              appearance="saas"
              tone="ghost"
              className="mr-auto"
              disabled={pending}
              onClick={() => {
                setError(null);
                setConfirmingRemoval(true);
              }}
              testId="member-term-remove"
            >
              {tw.removeTerm}
            </Button>
          ) : null}
          <Button
            appearance="saas"
            tone="secondary"
            disabled={pending}
            onClick={confirmingRemoval ? () => setConfirmingRemoval(false) : onClose}
            testId="member-term-cancel"
          >
            Cancel
          </Button>
          {confirmingRemoval ? (
            <Button
              appearance="saas"
              tone="danger"
              loading={remove.isPending}
              disabled={pending}
              onClick={() => remove.mutate()}
              testId="member-term-remove-confirm"
            >
              {tw.removeIt}
            </Button>
          ) : (
            <Button
              appearance="saas"
              tone="primary"
              loading={save.isPending}
              disabled={pending || problem !== null || unchanged}
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              testId="member-term-save"
            >
              {correcting ? tw.saveIt : tw.recordIt}
            </Button>
          )}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
